/* eslint-disable no-nested-ternary */
/* eslint-disable max-lines */

import tokens from '@kluein/design-tokens';
import { Box, Button, Text } from '@kluein/klue-ui';
import {
  DotsThreeOutlineVertical,
  House,
  List,
  PaperPlaneTilt,
  Sparkle,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { StringParam, useQueryParams } from 'use-query-params';

import { UserMenu } from 'components/layout/user-menu';
import { useAuth } from 'contexts/auth';
import { useEmbedded } from 'contexts/ui/embedded-context';
import { useResponsive } from 'hooks/use-responsive';
import { useTransitionNavigation } from 'hooks/use-transition-navigation';
import { SIDEBAR_SHORTCUT } from 'lib/hotkeys';
import { APP_V1_BASEURL } from 'lib/urls';
import store from 'store';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import { APP_NAV_BAR_BORDER_WIDTH, APP_NAV_BAR_HEIGHT } from './constants';
import { AppNavBarMenu } from './partials/app-nav-bar-menu';

import { AppSideMenu } from '../app-side-menu';
import {
  NavBar,
  NavBarAvatar,
  NavBarGlobalSearch,
  NavBarUnifiedSearch,
  NavBarLink,
} from '../nav-bar';
import { QuickSwitcher } from '../quick-switcher';

const StyledAvatarBox = styled(Box)<{ isImpersonating?: boolean }>`
  &:hover {
    ${({ isImpersonating }) =>
      `
      background-color: ${
        isImpersonating
          ? tokens.color.primaryintel.dark
          : tokens.color.neutral.lightgrey.main
      }
    `}
  }
`;

const QuickSwitcherLink = styled(Box)<{ isActive?: boolean }>`
  position: relative;

  svg {
    fill: ${tokens.color.neutral.black.main};
    stroke: ${tokens.color.neutral.black.main};
  }

  &:hover {
    background-color: ${tokens.color.neutral.lightgrey.main};
  }

  &:focus,
  &:focus-visible {
    border: none;
    box-shadow: none;
    outline: none;
  }
  &:focus-visible > div {
    border-radius: 2px;
    box-shadow: 0 0 2px 2px ${tokens.color.primary.main};
  }

  ${({ isActive }) =>
    isActive &&
    `
    background-color: ${tokens.color.neutral.lightgrey.main};
  `}
`;

const QueryParamConfig = {
  nav: StringParam,
  action: StringParam,
};

type linkProps = {
  to: string;
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
};

const ActiveAppNavBar = () => {
  const [{ nav }, setQuery] = useQueryParams(QueryParamConfig);

  const { pathname } = useLocation();
  const hasQuickSwitcherAccess = matchRoutes(
    [
      { path: '/' },
      { path: '/dashboard' },
      { path: '/ask-klue' },
      { path: '/profile/:id/*' },
      { path: '/search/*' },
      { path: '/company/*' },
    ],
    pathname,
  );

  const {
    homeRoute,
    isCurator,
    isAdmin,
    isImpersonating,
    user,
    isWinLossEnabled,
    isWinLossExclusive,
    isFeedHiddenInConsumerNavEnabled,
    isUnifiedSearchEnabled,
    isAskKlueDashboardPageEnabled,
  } = useAuth();
  const navigate = useNavigate();
  const { navigateWithTransition } = useTransitionNavigation();

  const { t } = useTranslation(['Common', 'Alerts']);
  const dispatch = useDispatch();
  const { maxWidth } = useResponsive();

  const [isQuickSwitcherOpen, setQuickSwitcherOpen] = useState(false);
  const [isUserMenuOpen, setUserMenuOpen] = useState(false);
  const showingAskKluePage =
    pathname.includes('ask-klue') && isAskKlueDashboardPageEnabled;

  const handleBoardsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Use transition animation if coming from ask-klue page
    if (pathname.includes('/ask-klue')) {
      navigateWithTransition('/dashboard', 'askklue-to-dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const visibilityGroupId = useSelector(store.select.auth.visibilityGroupId);

  const feedLinkProps: linkProps = {
    to: `${APP_V1_BASEURL}/feed`,
  };
  const digestLinkProps: linkProps = {
    to: `${APP_V1_BASEURL}/digest/overview`,
  };
  if (visibilityGroupId !== null) {
    feedLinkProps.onClick = (e) => {
      dispatch.auth.setPreviewExitInfoDialog({
        destination: 'news feed',
        redirectUrl: feedLinkProps.to,
      });
      e.preventDefault();
    };
    digestLinkProps.onClick = (e) => {
      dispatch.auth.setPreviewExitInfoDialog({
        destination: 'digest',
        redirectUrl: digestLinkProps.to,
      });
      e.preventDefault();
    };
  }

  useHotkeys(
    SIDEBAR_SHORTCUT,
    (event) => {
      event.preventDefault();
      setQuickSwitcherOpen(true);
    },
    [nav],
  );

  const renderNavbarLinks = () => {
    if (isCurator) {
      return (
        <Box
          key="appNavBar.navBarLink.container"
          data-test-id={TEST_IDS.appNavBar.newCuratorNavBarLink}
        >
          <AppNavBarMenu />
        </Box>
      );
    }

    if (maxWidth.medium) return null;

    return (
      <Box direction="row" key="appNavBar.navBarLink.container">
        {isWinLossEnabled && (
          <NavBarLink
            label={t('Common:winLoss')}
            title={t('Common:winLoss')}
            to="/win-loss"
            data-tracking-id={TRACKING_IDS.winLoss.globalNav}
          />
        )}

        {isFeedHiddenInConsumerNavEnabled ? null : (
          <NavBarLink
            label={t('Common:feed')}
            title={t('Common:feed')}
            data-test-id={TEST_IDS.appNavBar.navBarLinks.feed}
            data-tracking-id={TRACKING_IDS.global.appNavBar.feed}
            {...feedLinkProps}
          />
        )}

        <NavBarLink
          label={t('Common:digest')}
          title={t('Common:digest')}
          data-test-id={TEST_IDS.appNavBar.navBarLinks.digest}
          data-tracking-id={TRACKING_IDS.global.appNavBar.digest}
          {...digestLinkProps}
        />
      </Box>
    );
  };

  const navItems = [
    [
      hasQuickSwitcherAccess ? (
        <QuickSwitcherLink
          isActive={isQuickSwitcherOpen}
          data-test-id={TEST_IDS.layout.appNavBar.hamburgerMenuButton}
          data-tracking-id={TRACKING_IDS.global.appNavBar.quickSwitcher}
          key="appNavBar.hamburgerMenuButton"
          onClick={(event: React.MouseEvent) => {
            event && event.preventDefault();
            setQuickSwitcherOpen(true);
          }}
          a11yTitle={t('Common:navBar.hamburgerMenu.title')}
        >
          <Box
            align="center"
            direction="row"
            fill="vertical"
            justify="center"
            width="xsmall"
            margin="xxsmall"
          >
            <List
              color={
                isQuickSwitcherOpen
                  ? tokens.color.neutral.white.main
                  : tokens.color.neutral.lightgrey.main
              }
              size={tokens.iconSize.small}
            />
          </Box>
        </QuickSwitcherLink>
      ) : null,
      // When Ask Klue dashboard is enabled, show separate ASK KLUE and BOARDS links
      // Otherwise, show HOME
      isAskKlueDashboardPageEnabled && !isWinLossExclusive ? (
        <NavBarLink
          className={maxWidth.medium ? 'navBar-home-icon' : 'navBar-home'}
          data-tracking-id={TRACKING_IDS.global.appNavBar.askKlue}
          key="appNavBar.navBarLink.askKlueButton"
          title={t('Common:navBar.askKlue', {
            instance: isImpersonating ? `(${user?.companyName})` : '',
          })}
          to="/ask-klue"
        >
          <Box width={{ min: 'xsmall' }} align="center">
            <Text size="large">
              {maxWidth.medium ? (
                <Sparkle color={tokens.color.neutral.black.main} />
              ) : (
                t('Common:navBar.askKlue', {
                  instance: isImpersonating ? `(${user?.companyName})` : '',
                })
              )}
            </Text>
          </Box>
        </NavBarLink>
      ) : null,
      isAskKlueDashboardPageEnabled && !isWinLossExclusive ? (
        <NavBarLink
          className={maxWidth.medium ? 'navBar-home-icon' : 'navBar-home'}
          data-tracking-id={TRACKING_IDS.global.appNavBar.boards}
          key="appNavBar.navBarLink.boardsButton"
          title={t('Common:navBar.boards')}
          to="/dashboard"
          onClick={handleBoardsClick}
        >
          <Box width={{ min: 'xsmall' }} align="center">
            <Text size="large">
              {maxWidth.medium ? (
                <House color={tokens.color.neutral.black.main} />
              ) : (
                t('Common:navBar.boards')
              )}
            </Text>
          </Box>
        </NavBarLink>
      ) : (
        <NavBarLink
          className={maxWidth.medium ? 'navBar-home-icon' : 'navBar-home'}
          data-tracking-id={TRACKING_IDS.global.appNavBar.home}
          key="appNavBar.navBarLink.homeButton"
          title={t('Common:navBar.home')}
          to={homeRoute}
        >
          <Box width={{ min: 'xsmall' }} align="center">
            <Text size="large">
              {maxWidth.medium ? (
                <House color={tokens.color.neutral.black.main} />
              ) : (
                t('Common:navBar.home', {
                  instance: isImpersonating ? `(${user?.companyName})` : '',
                })
              )}
            </Text>
          </Box>
        </NavBarLink>
      ),
    ],
    [
      // Show search components - render but hide on ask-klue page to reserve space for animation
      !isWinLossExclusive &&
        (isUnifiedSearchEnabled ? (
          <NavBarUnifiedSearch key="appNavBar.navBarUnifiedSearch" />
        ) : showingAskKluePage ? null : (
          <NavBarGlobalSearch key="appNavBar.navBarGlobalSearch" />
        )),
      isWinLossExclusive && isUnifiedSearchEnabled ? (
        <NavBarUnifiedSearch key="appNavBar.navBarUnifiedSearch" />
      ) : null,
    ],
    [
      !isWinLossExclusive ? renderNavbarLinks() : null,
      !isCurator && !isAdmin && (
        <Box justify="center" key="appNavBar.navBarLink.submitIntelButton">
          <Button
            variant="primary-action"
            size="small"
            leftSlot={<PaperPlaneTilt />}
            onClick={() => {
              setQuery({ action: 'shareIntel' });
            }}
            label={
              !maxWidth.medium ? t('Common:navBar.submitIntel') : undefined
            }
            data-tracking-id={TRACKING_IDS.global.appNavBar.submitIntel}
          />
        </Box>
      ),

      <StyledAvatarBox
        direction="row"
        justify="center"
        align="center"
        pad={{ right: 'small', left: 'xxsmall' }}
        margin={{ left: 'xsmall' }}
        gap="small"
        key="appNavBar.navBarAvatar"
        background={isImpersonating ? tokens.color.primaryintel.main : 'none'}
        isImpersonating={isImpersonating}
      >
        <NavBarAvatar
          borderColor={
            isImpersonating
              ? tokens.color.primary.contrastmain
              : tokens.color.primary.contrastlight
          }
        />
        <Button
          variant="plain"
          focusIndicator={false}
          onClick={() => setUserMenuOpen(true)}
          data-tracking-id={TRACKING_IDS.global.appNavBar.userMenu}
        >
          <DotsThreeOutlineVertical
            size={tokens.iconSize.small}
            color={
              isImpersonating
                ? tokens.color.primary.contrastmain
                : tokens.color.primary.contrastlight
            }
            weight="fill"
          />
        </Button>
      </StyledAvatarBox>,
    ],
  ].map((items) => items.filter(Boolean));

  return (
    <NavBar
      border={{
        side: 'bottom',
        size: APP_NAV_BAR_BORDER_WIDTH,
        color: showingAskKluePage
          ? tokens.color.neutral[300]
          : tokens.color.neutral.lightgrey.main,
      }}
      background={isImpersonating ? tokens.color.primaryintel.light : undefined}
    >
      <Box
        direction="row"
        gridArea="col1"
        justify="start"
        height={APP_NAV_BAR_HEIGHT}
      >
        {navItems[0]}
      </Box>
      <Box
        alignSelf="center"
        gridArea="col2"
        pad={{
          horizontal: maxWidth.medium ? 'xlarge' : 'xsmall',
          vertical: 'xxsmall',
        }}
      >
        {navItems[1]}
      </Box>
      <Box direction="row" gridArea="col3" justify="end">
        {navItems[2]}
      </Box>
      <AppSideMenu
        position="left"
        isOpen={isQuickSwitcherOpen}
        onClose={() => setQuickSwitcherOpen(false)}
      >
        <QuickSwitcher onNavigate={() => setQuickSwitcherOpen(false)} />
      </AppSideMenu>
      <AppSideMenu
        position="right"
        isOpen={isUserMenuOpen}
        onClose={() => setUserMenuOpen(false)}
      >
        <UserMenu onClose={() => setUserMenuOpen(false)} />
      </AppSideMenu>
    </NavBar>
  );
};

const AppNavBar = () => {
  const isEmbedded = useEmbedded();

  if (isEmbedded) {
    return null;
  }

  return <ActiveAppNavBar />;
};

export default AppNavBar;
